import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import type { PropertyDetails } from '../api/types';
import { Button, ErrorNote, Field, Loading, useReport, useToast } from '../ui/kit';
import { HotelLogo } from '../ui/HotelLogo';

const MAX_LOGO_BYTES = 300_000;
const RASTER_TYPES = ['image/png', 'image/jpeg', 'image/webp'];

function toBase64(bytes: ArrayBuffer): string {
  const u8 = new Uint8Array(bytes);
  let s = '';
  for (let i = 0; i < u8.length; i += 0x8000) s += String.fromCharCode(...u8.subarray(i, i + 0x8000));
  return btoa(s);
}

/**
 * Turns whatever the person picked into something the server accepts: a vector (SVG) logo goes as it is; a photo or
 * screenshot is scaled down in the browser (a phone photo can be many megabytes) and sent as a PNG, which keeps transparency.
 */
export async function prepareLogo(file: File): Promise<string> {
  if (file.type === 'image/svg+xml') {
    if (file.size > MAX_LOGO_BYTES) throw new Error('That SVG is too large. Keep the logo under 300 KB.');
    return toBase64(await file.arrayBuffer());
  }
  if (!RASTER_TYPES.includes(file.type)) throw new Error('Choose a PNG, JPEG, WebP or SVG picture.');
  const bitmap = await createImageBitmap(file).catch(() => {
    throw new Error('That picture could not be opened.');
  });
  for (const side of [640, 480, 320]) {
    const scale = Math.min(1, side / Math.max(bitmap.width, bitmap.height));
    if (scale === 1 && file.size <= MAX_LOGO_BYTES) return toBase64(await file.arrayBuffer()); // already small: keep it exactly as it is
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(bitmap.width * scale));
    canvas.height = Math.max(1, Math.round(bitmap.height * scale));
    canvas.getContext('2d')!.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise<Blob | null>((res) => canvas.toBlob(res, 'image/png'));
    if (blob && blob.size <= MAX_LOGO_BYTES) return toBase64(await blob.arrayBuffer());
  }
  throw new Error('That picture is too detailed to use as a logo. Try a simpler or smaller one.');
}

/** Setup → Hotel: the name, contacts, tax and business registration numbers that are printed on receipts, and the logo. */
export function HotelDetails() {
  const { reloadMe } = useAuth();
  const qc = useQueryClient();
  const toast = useToast();
  const report = useReport();
  const q = useQuery({ queryKey: ['property'], queryFn: () => api<PropertyDetails>('/property') });
  const fileRef = useRef<HTMLInputElement>(null);

  const [f, setF] = useState({ name: '', phone: '', email: '', address: '', taxNumber: '', businessRegistration: '', checkInTime: '', checkOutTime: '' });
  useEffect(() => {
    const d = q.data;
    if (d) setF({ name: d.name, phone: d.phone ?? '', email: d.email ?? '', address: d.address ?? '', taxNumber: d.taxNumber ?? '', businessRegistration: d.businessRegistration ?? '', checkInTime: d.checkInTime, checkOutTime: d.checkOutTime });
  }, [q.data]);
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF((x) => ({ ...x, [k]: e.target.value }));

  const refresh = async () => {
    await qc.invalidateQueries({ queryKey: ['property'] });
    await reloadMe();
  };

  const save = useMutation({
    mutationFn: () => api('/property', { method: 'PATCH', body: { name: f.name, phone: f.phone, email: f.email, address: f.address, taxNumber: f.taxNumber, businessRegistration: f.businessRegistration, checkInTime: f.checkInTime, checkOutTime: f.checkOutTime } }),
    onSuccess: async () => {
      toast.ok('Hotel details saved');
      await refresh();
    },
    onError: report,
  });

  const upload = useMutation({
    mutationFn: async (file: File) => api('/property/logo', { method: 'PUT', body: { data: await prepareLogo(file) } }),
    onSuccess: async () => {
      toast.ok('Logo changed');
      await refresh();
    },
    onError: report,
  });
  const remove = useMutation({
    mutationFn: () => api('/property/logo', { method: 'DELETE' }),
    onSuccess: async () => {
      toast.ok('Logo removed; the built-in one is shown');
      await refresh();
    },
    onError: report,
  });

  if (q.isLoading) return <Loading />;
  if (q.error || !q.data) return <ErrorNote error={q.error} retry={() => void q.refetch()} />;

  const timeOk = (t: string) => /^([01]\d|2[0-3]):[0-5]\d$/.test(t);
  const emailOk = f.email.trim() === '' || /^\S+@\S+\.\S+$/.test(f.email.trim());
  const valid = f.name.trim().length >= 2 && timeOk(f.checkInTime) && timeOk(f.checkOutTime) && emailOk;

  return (
    <div className="stack">
      <div className="banner info">These details are printed on every receipt, invoice and restaurant bill. The tax number and business registration number are shown as typed: check them against your certificates.</div>

      <section className="card stack" aria-labelledby="logo-h">
        <h2 id="logo-h" className="h2">
          Logo
        </h2>
        <div className="row" style={{ alignItems: 'center', gap: 16 }}>
          <HotelLogo version={q.data.logoVersion} className="receipt-logo" alt="The hotel logo" />
          <div className="stack" style={{ gap: 8 }}>
            <span className="small muted">{q.data.logoVersion ? 'Your logo is shown on receipts, the sign-in page and the booking page.' : 'The built-in logo is shown. Upload your own to replace it.'}</span>
            <div className="row">
              <input
                ref={fileRef}
                type="file"
                accept="image/png,image/jpeg,image/webp,image/svg+xml"
                hidden
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  e.target.value = '';
                  if (file) upload.mutate(file);
                }}
              />
              <Button onClick={() => fileRef.current?.click()} loading={upload.isPending}>
                {q.data.logoVersion ? 'Change logo' : 'Upload logo'}
              </Button>
              {q.data.logoVersion ? (
                <Button variant="ghost" onClick={() => remove.mutate()} loading={remove.isPending}>
                  Remove
                </Button>
              ) : null}
            </div>
            <span className="small muted">PNG, JPEG, WebP or SVG. A square picture on a transparent or white background works best.</span>
          </div>
        </div>
      </section>

      <form
        className="card stack"
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          if (valid) save.mutate();
        }}
      >
        <Field id="hd-name" label="Hotel name">
          <input id="hd-name" className="input" value={f.name} onChange={set('name')} maxLength={80} />
        </Field>
        <div className="grid-2">
          <Field id="hd-tax" label="Tax number (TIN / VAT)" hint="Printed on receipts">
            <input id="hd-tax" className="input" value={f.taxNumber} onChange={set('taxNumber')} maxLength={40} autoComplete="off" />
          </Field>
          <Field id="hd-reg" label="Business registration" hint="Name and number, as on your certificate">
            <input id="hd-reg" className="input" value={f.businessRegistration} onChange={set('businessRegistration')} maxLength={60} autoComplete="off" />
          </Field>
        </div>
        <div className="grid-2">
          <Field id="hd-phone" label="Phone">
            <input id="hd-phone" className="input" inputMode="tel" value={f.phone} onChange={set('phone')} maxLength={30} />
          </Field>
          <Field id="hd-email" label="Email" error={emailOk ? undefined : 'Enter a valid email address'}>
            <input id="hd-email" className="input" type="email" value={f.email} onChange={set('email')} maxLength={120} />
          </Field>
        </div>
        <Field id="hd-address" label="Address">
          <input id="hd-address" className="input" value={f.address} onChange={set('address')} maxLength={200} />
        </Field>
        <div className="grid-2">
          <Field id="hd-in" label="Check-in from" hint="24-hour time. 00:00 means any time" error={timeOk(f.checkInTime) ? undefined : 'Use a time like 14:00'}>
            <input id="hd-in" className="input" value={f.checkInTime} onChange={set('checkInTime')} placeholder="14:00" />
          </Field>
          <Field id="hd-out" label="Check-out by" error={timeOk(f.checkOutTime) ? undefined : 'Use a time like 12:00'}>
            <input id="hd-out" className="input" value={f.checkOutTime} onChange={set('checkOutTime')} placeholder="12:00" />
          </Field>
        </div>
        <div>
          <Button variant="primary" type="submit" disabled={!valid} loading={save.isPending}>
            Save details
          </Button>
        </div>
      </form>
    </div>
  );
}
